"use client";

import { useEffect } from "react";

export const dynamic = "force-dynamic";

/**
 * The error page of last resort: it REPLACES the root layout when even that fails, so it brings
 * its own `<html lang="en">` and does not depend on anything the failed layout provided: no
 * translations, no stylesheet (inline styles), no components. English only on purpose
 * (public-pages-v2, WP-8; review C.15). It offers a way out: try again, or the home page.
 */
const PAGE: React.CSSProperties = {
  margin: 0,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2rem 1rem",
  background: "#fdf8f4",
  color: "#3d372e",
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  textAlign: "center",
};
const BUTTON: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "44px",
  padding: "0 1.25rem",
  borderRadius: "0.5rem",
  border: "1px solid #4a4236",
  background: "#4a4236",
  color: "#ffffff",
  fontSize: "0.875rem",
  fontWeight: 500,
  cursor: "pointer",
  textDecoration: "none",
};
const LINK: React.CSSProperties = { ...BUTTON, background: "#ffffff", color: "#4a4236" };

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Skip: instrumentation-client.ts already auto-reloads on ChunkLoadError.
    // Capturing here too doubles the Sentry events for post-deploy stale tabs.
    const isChunk =
      error?.name === "ChunkLoadError" ||
      /ChunkLoadError|Loading chunk|Failed to (load|fetch) (module|chunk)/i.test(
        error?.message || ""
      );
    if (isChunk) return;
    void import("@sentry/nextjs").then((Sentry) => {
      Sentry.captureException(error, {
        tags: { boundary: "global-error" },
        contexts: {
          nextjs: { digest: error.digest },
          page: typeof window !== "undefined"
            ? { url: window.location.href, pathname: window.location.pathname }
            : undefined,
        },
      });
    }).catch(() => {});
  }, [error]);

  return (
    <html lang="en">
      <body style={PAGE}>
        <main style={{ maxWidth: "28rem" }}>
          <h1 style={{ margin: "0 0 0.75rem", fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.3 }}>
            Something went wrong
          </h1>
          <p style={{ margin: "0 0 1.5rem", lineHeight: 1.6 }}>
            We ran into an unexpected problem. You can try again, or go to the home page.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", justifyContent: "center" }}>
            <button type="button" onClick={() => reset()} style={BUTTON}>
              Try again
            </button>
            {/* A plain link on purpose: the router may be what failed. */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a href="/" style={LINK}>
              Go to the home page
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
